import {
  useState,
  useEffect,
  useCallback,
  useImperativeHandle,
  forwardRef,
  useMemo,
} from 'react';
import { Box, Flex, Button, Spinner } from '@chakra-ui/react';
import { CollapseSection } from '@/components/ats';
import { API_SERVICE } from '@/pages/settings/org/components/integrations/hris_onboarding/api/api';
import { FolderEntry } from './FolderEntry';
import { SECTION_WRAPPER_PROPS, EMPTY_ENTRY } from './constants';
import { Divider, mapDocForSubmit } from './helpers';

const DocumentsSection = forwardRef(({ integration, applicationId }, ref) => {
  const [categories, setCategories] = useState([]);
  const [selectableAttachments, setSelectableAttachments] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [documentEntries, setDocumentEntries] = useState([{ ...EMPTY_ENTRY }]);

  useEffect(() => {
    const fetchCategories = async () => {
      if (!integration?.type) return;
      setIsLoading(true);
      try {
        const response = await API_SERVICE.getEmployeeDocumentCategories(
          integration.type,
          applicationId
        );
        if (response) {
          setCategories(response.employee_document_categories || []);
          setSelectableAttachments(response.selectable_attachments || []);
        }
      } catch (error) {
        console.error('Failed to fetch document categories:', error);
      } finally {
        setIsLoading(false);
      }
    };
    fetchCategories();
  }, [integration?.type, applicationId]);

  const handleUpdateEntry = useCallback((index, updatedEntry) => {
    setDocumentEntries((prev) =>
      prev.map((entry, i) => (i === index ? updatedEntry : entry))
    );
  }, []);

  const handleAddEntry = useCallback(() => {
    setDocumentEntries((prev) => [...prev, { ...EMPTY_ENTRY }]);
  }, []);

  const selectedFolderIds = useMemo(
    () => documentEntries.map((entry) => entry.folderId).filter(Boolean),
    [documentEntries]
  );

  useImperativeHandle(
    ref,
    () => ({
      getDocumentsData: () =>
        documentEntries
          .filter((entry) => entry.folderId && entry.documents.length > 0)
          .map((entry) => ({
            category_id: entry.folderId,
            files: entry.documents.map(mapDocForSubmit),
          })),
    }),
    [documentEntries]
  );

  return (
    <Box mt="8px">
      <CollapseSection {...SECTION_WRAPPER_PROPS}>
        {isLoading ? (
          <Flex justifyContent="center" alignItems="center" py="24px">
            <Spinner size="sm" color="ats-primary" />
          </Flex>
        ) : (
          <Box>
            {documentEntries.map((entry, index) => (
              <FolderEntry
                key={index}
                entry={entry}
                index={index}
                categories={categories}
                selectableAttachments={selectableAttachments}
                onUpdateEntry={handleUpdateEntry}
                selectedFolderIds={selectedFolderIds}
                integrationName={integration?.name}
              />
            ))}

            <Flex alignItems="center" mt="24px" mb="8px" w="100%">
              <Divider />
              <Button
                bg="primary-50"
                color="primary-500"
                size="sm"
                h="36px"
                px="16px"
                mx="16px"
                borderRadius="6px"
                onClick={handleAddEntry}
                fontWeight="500"
                _hover={{ bg: 'primary-100' }}
              >
                Select additional folders
              </Button>
              <Divider />
            </Flex>
          </Box>
        )}
      </CollapseSection>
    </Box>
  );
});

DocumentsSection.displayName = 'DocumentsSection';

export default DocumentsSection;
